import React, { useState, useEffect, useCallback, useMemo, memo } from "react";

const ProductClassificationSelect = memo(function ProductClassificationSelect({
  classificationTree: initialClassificationTree,
  leafIndex = 0,
  railsFormName = "",
  handleLeafSelected = () => {},
}) {
  const [classificationTree, setClassificationTree] = useState(
    initialClassificationTree
  );

  useEffect(() => {
    setClassificationTree(initialClassificationTree);
  }, [initialClassificationTree]);

  const handleChange = useCallback(
    (event) => {
      const selectedValue = event.target.value;
      const updatedTree = { ...classificationTree, selectedValue };

      const selectedNode = updatedTree.selectOptions?.[selectedValue];
      const isLeafSelected =
        selectedNode && Object.keys(selectedNode.selectOptions || {}).length === 0;

      if (isLeafSelected) {
        handleLeafSelected(selectedValue, selectedNode?.label);
      }

      setClassificationTree(updatedTree);
    },
    [classificationTree, handleLeafSelected]
  );

  // Memoize sorted options to avoid re-sorting on every render
  const sortedOptions = useMemo(() => {
    const selectOptions = classificationTree?.selectOptions || {};
    return Object.values(selectOptions).sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [classificationTree?.selectOptions]);

  const selectFieldName = `${railsFormName}[product_classification_ids][]`;
  const selectId = `product-classification-leaf-${leafIndex}`;
  const selectedValue = classificationTree?.selectedValue || "";
  const selectedLeaf = classificationTree?.selectOptions?.[selectedValue];
  const shouldRenderNextDropdown =
    selectedLeaf && Object.keys(selectedLeaf.selectOptions || {}).length > 0;
  const isLeafSelected =
    selectedLeaf && Object.keys(selectedLeaf.selectOptions || {}).length === 0;

  const additionalClassName = selectedLeaf
    ? "classified-successfully"
    : "form-control";

  return (
    <>
      <div className="form-group">
        <label htmlFor={selectId} className="sr-only">
          Select category level {leafIndex + 1}
        </label>
        <select
          id={selectId}
          name={selectFieldName}
          required
          value={selectedValue}
          onChange={handleChange}
          className={`form-control custom-select custom-select-lg ${additionalClassName}`}
          aria-describedby={isLeafSelected ? `${selectId}-success` : undefined}
        >
          <option value="">Select category</option>
          {sortedOptions.map(({ id, label }) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        {isLeafSelected && (
          <div
            id={`${selectId}-success`}
            className="valid-feedback is-valid"
            role="status"
            aria-live="polite"
          >
            Successfully classified!
          </div>
        )}
      </div>
      {shouldRenderNextDropdown && (
        <ProductClassificationSelect
          key={selectedValue}
          classificationTree={selectedLeaf}
          leafIndex={leafIndex + 1}
          railsFormName={railsFormName}
          handleLeafSelected={handleLeafSelected}
        />
      )}
    </>
  );
});

export default ProductClassificationSelect;
